<script setup lang="ts">
import Date from "./Date.vue";
import Leagues from "./Leagues.vue";
import TypeSort from "./TypeSort.vue";
import Odds from "./Odds.vue";
import Tabs from "~/components/ui/Tabs.vue";
import { useBetbook } from "~/store/betbook";
import { useSettings } from "~/store/settings";
import { ISport } from "~/types/sports/sport";
import t from "~/utils/translate";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";

interface Props {
	sport?: ISport;
}

defineProps<Props>();
const emits = defineEmits(["go-to-highlights", "close"]);
const store = useBetbook();
const settings = useSettings();
const betbookSignalR = useSignalR("betBook");
const ledger = useLedger();
const tab = ref<"type-sort" | "leagues" | "date" | "odds">(typeof store.filterModal === "string" ? store.filterModal || "type-sort" : "type-sort");
const route = useRoute();
const { fixtureType } = route.params;

interface state {
	regionAndLeagueIds: { regionId: string; leagueId: string }[] | undefined;
	fromStartEpoch: number | undefined;
	toStartEpoch: number | undefined;
	odds:
		| {
				maximum: number;
				minimum: number;
		  }
		| undefined;
	sortResults: {
		league: boolean;
		time: boolean;
	};
}

const initialState: state = {
	regionAndLeagueIds: [],
	fromStartEpoch: undefined,
	toStartEpoch: undefined,
	odds: undefined,
	sortResults: {
		league: true,
		time: false,
	},
};

const filter = reactive({ ...initialState });

const currentRegionAndLeagueCheck = ref();

onMounted(() => {
	currentRegionAndLeagueCheck.value = store.filters.regionAndLeagueIds?.map((x) => `${x.leagueId}-${x.regionId}`);
	filter.regionAndLeagueIds = store.filters.regionAndLeagueIds || [];
	filter.sortResults = { league: store.filters.sortOrder === "League", time: store.filters.sortOrder === "Time" };
	filter.fromStartEpoch = store.filters.fromStartEpoch;
	filter.toStartEpoch = store.filters.toStartEpoch;
	filter.odds = store.filters.odds;
});

const handleReset = (closeModal = true) => {
	filter.sortResults = initialState.sortResults;
	filter.regionAndLeagueIds = initialState.regionAndLeagueIds;
	filter.fromStartEpoch = initialState.fromStartEpoch;
	filter.toStartEpoch = initialState.toStartEpoch;
	filter.odds = initialState.odds;
	handleSubmit(closeModal);
};

const handleRemoveRegion = (val: { leagueId: string; regionId: string }) => {
	if (filter.regionAndLeagueIds)
		filter.regionAndLeagueIds = [...filter?.regionAndLeagueIds.filter((x) => !(x.leagueId === val.leagueId && x.regionId === val.regionId))];
};

const handleSubmit = (closeModal = true) => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		});
	}
	const query: { [x: string]: any } = {};
	// handle sort results
	if (filter.sortResults.league) {
		store.filters.sortOrder = "League";
		query.sortOrder = "League";
	} else if (filter.sortResults.time) {
		store.filters.sortOrder = "Time";
		query.sortOrder = "Time";
	}
	// handle time epoch
	store.filters.fromStartEpoch = filter.fromStartEpoch;
	if (filter.fromStartEpoch) query.fromStartEpoch = filter.fromStartEpoch;
	store.filters.toStartEpoch = filter.toStartEpoch;
	if (filter.toStartEpoch) query.toStartEpoch = filter.toStartEpoch;
	// handle region
	store.filters.regionAndLeagueIds = filter.regionAndLeagueIds && filter.regionAndLeagueIds.length > 0 ? filter.regionAndLeagueIds : undefined;
	if (filter.regionAndLeagueIds && filter.regionAndLeagueIds.length > 0) {
		const leagues: string[] = [];
		filter.regionAndLeagueIds.map((r) => leagues.push(`${r.regionId}_${r.leagueId}`));
		query.selectedLeagues = leagues.join();
	}
	// handle odds
	store.filters.odds = filter.odds;
	if (filter.odds?.minimum) {
		query.minimumOdds = filter.odds.minimum;
	}
	if (filter.odds?.maximum) {
		query.maximumOdds = filter.odds.maximum;
	}
	// handle route
	store.applyingFilters = true;
	emits("go-to-highlights", query);

	if (confirmModal.value) confirmModal.value = false;
	if (closeModal) store.filterModal = false;
};

const canReset = computed(() => {
	return (
		(filter.regionAndLeagueIds?.length && filter.regionAndLeagueIds?.length > 0) ||
		filter.fromStartEpoch ||
		filter.toStartEpoch ||
		filter.odds ||
		filter.sortResults.time
	);
});

const hasChanged = computed(() => {
	let leagueChange = false;
	let fromStartEpochChange = false;
	let toStartEpochChange = false;
	let oddsChange = false;
	let sortResultsChange = false;

	if (!currentRegionAndLeagueCheck.value && filter.regionAndLeagueIds?.length && filter.regionAndLeagueIds?.length > 0) {
		leagueChange = true;
	} else if (!currentRegionAndLeagueCheck.value && filter.regionAndLeagueIds?.length === 0) {
		leagueChange = false;
	} else if (currentRegionAndLeagueCheck.value?.length !== filter.regionAndLeagueIds?.length) {
		leagueChange = true;
	} else {
		filter.regionAndLeagueIds?.forEach((x) => {
			if (!currentRegionAndLeagueCheck.value?.includes(`${x.leagueId}-${x.regionId}`)) leagueChange = true;
		});
	}

	if (filter.fromStartEpoch || store.filters.fromStartEpoch) {
		toStartEpochChange = filter.fromStartEpoch !== store.filters.fromStartEpoch;
	}

	if (filter.toStartEpoch || store.filters.toStartEpoch) {
		fromStartEpochChange = filter.toStartEpoch !== store.filters.toStartEpoch;
	}

	if (filter.odds || store.filters.odds) {
		oddsChange = filter.odds?.minimum !== store.filters.odds?.minimum || filter.odds?.maximum !== store.filters.odds?.maximum;
	}

	sortResultsChange =
		filter.sortResults.league !== (store.filters.sortOrder === "League") || filter.sortResults.time !== (store.filters.sortOrder === "Time");

	return leagueChange || fromStartEpochChange || toStartEpochChange || oddsChange || sortResultsChange;
});

const confirmModal = ref(false);

const handleClose = (val: boolean) => {
	if (!val && hasChanged.value) {
		confirmModal.value = true;
	} else {
		store.filterModal = val;
	}
};

const handleConfirm = (confirm: boolean) => {
	if (confirm) {
		handleSubmit();
	} else {
		confirmModal.value = false;
		store.filterModal = false;
	}
};
</script>

<template>
	<UiModal :content-padding="false" size="small" :title="t('filters')" :active="!!store.filterModal && !confirmModal" @set-active="handleClose">
		<div class="flex justify-center p-2">
			<Tabs
				class="flex w-auto"
				:active="tab"
				@on-update="(val) => (tab = val)"
				:tabs="[
					{ name: `${t('type')} / ${t('sort')}`, value: 'type-sort' },
					{ name: t('leagues'), value: 'leagues' },
					{ name: t('date'), value: 'date' },
					{ name: t('odds'), value: 'odds' },
				]"
			/>
		</div>
		<div class="overflow-x-hidden overflow-y-scroll md:h-80 scrollbar-hidden">
			<TypeSort v-if="tab === 'type-sort'" :filter="filter" @update-sort-results="(val) => (filter.sortResults = val)" />
			<Leagues
				v-else-if="tab === 'leagues'"
				:filter="filter"
				:topLeagues="sport?.topLeagues"
				@update-regions="(val) => (filter.regionAndLeagueIds = [...(filter.regionAndLeagueIds || []), val])"
				@remove-region="(val) => handleRemoveRegion(val)"
			/>
			<Date
				v-else-if="tab === 'date'"
				:filter="filter"
				@update-from-epoch="(val) => (filter.fromStartEpoch = val)"
				@update-to-epoch="(val) => (filter.toStartEpoch = val)"
			/>
			<Odds v-else-if="tab === 'odds'" :filter="filter" @update-odds="(val) => (filter.odds = val)" />
		</div>
		<template #footer>
			<div class="flex justify-between w-full">
				<UiButton @click="() => handleReset(false)" class="w-[48%]" variant="secondary" :label="t('reset')" :disabled="!canReset" />
				<UiButton @click="() => handleSubmit()" class="w-[48%]" variant="identity" :label="t('apply')" :disabled="!hasChanged" />
			</div>
		</template>
	</UiModal>
	<UiModal :title="t('filters')" size="small" :active="confirmModal" @set-active="() => handleConfirm(false)">
		<div>{{ t("filter-confirmation") }}</div>
		<template #footer>
			<div class="flex justify-between w-full">
				<UiButton @click="() => handleConfirm(false)" class="w-[48%]" variant="secondary" :label="t('no')" />
				<UiButton @click="() => handleConfirm(true)" class="w-[48%]" variant="identity" :label="t('yes')" />
			</div>
		</template>
	</UiModal>
</template>
